import React, { useState } from 'react';
import dateFormat from 'dateformat';
import { Badge, Flex, Text } from '@chakra-ui/react';
import { OutlineCard } from '../MySubmission/PreferencesTab';
import { ButtonText } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faUpload,
  faDownload,
  faArrowLeft
} from '@fortawesome/pro-regular-svg-icons';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { Overlay } from 'src/components/Overlay';
import { Layout } from 'src/components/Layout';
import { SubmissionInfoCard } from 'src/components/SubmissionInfo';
import { ReadonlyCopyrightForm } from '../CopyrightForm/ReadonlyCopyrightForm';
import { EditorialUploadCopyrightFormModal } from '../CopyrightForm/EditorialUploadCopyrightForm';
import { OverwriteCopyrightAgreementModal } from '../CopyrightForm/OverwriteCopyrightModal';
import { EditorialRequestCopyrightFormModal } from '../CopyrightForm/EditorialRequestCopyright';
import { faFileLines } from '@fortawesome/pro-light-svg-icons';
import { openFile } from 'src/utils/file';
import ProtectedWorkflowAction from '../../ProtectedAction/ProtectedWorkflowAction';

const CopyrightAgreementDetails: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const { copyrightForm } = useSubmissionData();

  return (
    <Overlay>
      <Flex flexDirection="column" backgroundColor="surfaceSecondaryFill">
        <Layout width="48rem">
          {copyrightForm && (
            <Flex flexDirection="column" gap={8}>
              <ButtonText
                leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
                maxWidth="fit-content"
                onClick={onClose}
                variant="secondary"
              >
                Back
              </ButtonText>
              <Flex flexDirection="column" gap={2}>
                <Text textStyle="headingTitle">
                  Copyright and Publication Agreement
                </Text>
                <Text>
                  {copyrightForm.type ? 'Sent by' : 'Uploaded by'}{' '}
                  {copyrightForm.submittedBy}, on{' '}
                  {dateFormat(copyrightForm.date, 'mmmm d, yyyy')}{' '}
                </Text>
              </Flex>
              <Flex
                flexDirection="column"
                padding={6}
                borderRadius="base"
                background="neutralPrimaryFill"
              >
                <SubmissionInfoCard />
              </Flex>
              {copyrightForm.type && <ReadonlyCopyrightForm />}
            </Flex>
          )}
        </Layout>
      </Flex>
    </Overlay>
  );
};

const CopyrightStatusDescription: React.FC = () => {
  const {
    copyrightForm,
    workflowResponse: {
      workflowData: { copyrightForm: copyrightFormWorkflow }
    },
    availableActions
  } = useSubmissionData();

  if (copyrightFormWorkflow?.submitted) {
    const submittedCopyrightDescription = copyrightForm?.type
      ? 'The author has provided a Copyright and Publication Agreement'
      : 'A Copyright and Publication Agreement was uploaded by the Editors';
    return (
      <Text>
        {submittedCopyrightDescription} on{' '}
        {dateFormat(copyrightForm?.date, 'mmmm d, yyyy')}.
      </Text>
    );
  }

  if (copyrightFormWorkflow?.requested) {
    return (
      <Text textStyle="textBody">
        An agreement was requested – the Author will be informed to take action
        via email.
      </Text>
    );
  }

  if (availableActions.contains('EditorialRequestCopyright')) {
    return (
      <Text textStyle="textBody">
        No Copyright and Publication Agreement has been provided – request the
        Author to provide this document to proceed with publication.
      </Text>
    );
  }

  return (
    <>
      <Text textStyle="textBody">
        Copyright and Publication Agreements become available for Authors in the
        Revision stage.
      </Text>
      <Text textStyle="textBody">
        If the authors have not provided this information by the time the
        submission enters pre-production, you will be able to request the form
        entirely, or request additional edits.
      </Text>
    </>
  );
};

const CopyrightAndPublicationAgreement: React.FC = () => {
  const {
    availableActions,
    workflowResponse: {
      workflowData: { copyrightForm: copyrightFormWorkflow }
    },
    copyrightForm,
    submissionId,
    submissionContextId,
    organizationId
  } = useSubmissionData();

  const [isViewAgreement, setIsViewAgreement] = useState<boolean>(false);
  const [isRequestAgreementModalEnabled, setIsRequestAgreementModalEnabled] =
    useState<boolean>(false);
  const [isUploadCopyrightModalEnabled, setIsUploadCopyrightModalEnabled] =
    useState<boolean>(false);
  const [
    isOverwriteAgreementModalEnabled,
    setIsOverwriteAgreementModalEnabled
  ] = useState<'upload' | 'request' | ''>('');

  const displayViewAgreementButton = copyrightFormWorkflow?.submitted;

  const displayManualUploadButton = availableActions.contains(
    'EditorialUploadCopyright'
  );
  const displayRequestAgreementButton = availableActions.contains(
    'EditorialRequestCopyright'
  );
  const downloadAgreementButton = !!copyrightForm?.file?.url;

  const displayButtons =
    displayViewAgreementButton ||
    displayRequestAgreementButton ||
    displayManualUploadButton ||
    downloadAgreementButton;

  return (
    <OutlineCard>
      {isViewAgreement && (
        <CopyrightAgreementDetails onClose={() => setIsViewAgreement(false)} />
      )}
      {isUploadCopyrightModalEnabled && (
        <EditorialUploadCopyrightFormModal
          onClose={() => setIsUploadCopyrightModalEnabled(false)}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={submissionContextId}
        />
      )}
      {isOverwriteAgreementModalEnabled && (
        <OverwriteCopyrightAgreementModal
          onClose={() => setIsOverwriteAgreementModalEnabled('')}
          onSubmit={() => {
            if (isOverwriteAgreementModalEnabled === 'upload') {
              setIsUploadCopyrightModalEnabled(true);
            }
            if (isOverwriteAgreementModalEnabled === 'request') {
              setIsRequestAgreementModalEnabled(true);
            }

            setIsOverwriteAgreementModalEnabled('');
          }}
        />
      )}
      {isRequestAgreementModalEnabled && (
        <EditorialRequestCopyrightFormModal
          onClose={() => setIsRequestAgreementModalEnabled(false)}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={submissionContextId}
        />
      )}
      <Flex flexDirection="column" gap={4}>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingGroup">
            Copyright and Publication Agreement
          </Text>
          <CopyrightStatusDescription />
        </Flex>
        {displayButtons && (
          <Flex gap={6} flexWrap="wrap" alignItems="center">
            {displayViewAgreementButton && (
              <>
                {copyrightForm?.type ? (
                  <ButtonText
                    onClick={() => setIsViewAgreement(true)}
                    disabled={false}
                    iconLeft={<FontAwesomeIcon icon={faFileLines} />}
                  >
                    View Agreement
                  </ButtonText>
                ) : null}
              </>
            )}
            {downloadAgreementButton && (
              <Flex justifyContent="flex-start">
                <ButtonText
                  variant="secondary"
                  onClick={() => {
                    copyrightForm.file?.url && openFile(copyrightForm.file.url);
                  }}
                  leftIcon={<FontAwesomeIcon icon={faDownload} />}
                >
                  Download Full Agreement
                </ButtonText>
              </Flex>
            )}
            {displayRequestAgreementButton &&
              (!copyrightFormWorkflow?.requested ? (
                <ProtectedWorkflowAction actionName="EditorialRequestCopyright">
                  <ButtonText
                    onClick={() => {
                      if (copyrightFormWorkflow?.submitted) {
                        setIsOverwriteAgreementModalEnabled('request');
                      } else {
                        setIsRequestAgreementModalEnabled(true);
                      }
                    }}
                    disabled={false}
                    iconLeft={<FontAwesomeIcon icon={faEdit} />}
                  >
                    Request Agreement
                  </ButtonText>
                </ProtectedWorkflowAction>
              ) : (
                <Badge size="base" variant="neutral">
                  <Flex gap={1} alignItems="center">
                    <FontAwesomeIcon icon={faEdit} />{' '}
                    <Text textStyle="headingItem">Request Agreement</Text>
                  </Flex>
                </Badge>
              ))}
            {displayManualUploadButton && (
              <ProtectedWorkflowAction actionName="EditorialUploadCopyright">
                <ButtonText
                  onClick={() => {
                    if (copyrightFormWorkflow?.submitted) {
                      setIsOverwriteAgreementModalEnabled('upload');
                    } else {
                      setIsUploadCopyrightModalEnabled(true);
                    }
                  }}
                  disabled={false}
                  iconLeft={<FontAwesomeIcon icon={faUpload} />}
                >
                  Upload Agreement Manually
                </ButtonText>
              </ProtectedWorkflowAction>
            )}
          </Flex>
        )}
      </Flex>
    </OutlineCard>
  );
};

export default CopyrightAndPublicationAgreement;
